import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import Swal from 'sweetalert2';
import EmojiPicker from 'emoji-picker-react';
import { apiHandler } from '../../../api/apihandler';
import {
  CHAT_SAVE,
  CHAT_SINGLE_USER,
  CHAT_READ_ALL,
  CHAT_DELETE,
  CHAT_EDIT,
  CHAT_REACT,
  CHAT_REPLY,
  CHAT_MEDIA_SAVE,
  CHAT_USERS,
  GROUP_CHAT_SAVE,
} from '../../../api/constants';
import { handleImageUpload } from '../../../api/uploads';
import { user_data as userDataSelector } from '../../../store/setting/selectors.ts';
import { apiSucceeded } from '../feed/feedUtils';
import {
  NO_IMAGE,
  getCurrentUser,
  fullName,
  formatChatTime,
  getMessageText,
  getReplyMessageData,
  getReactionSummary,
  REACTION_OPTIONS,
} from './chatUtils';
import EmptyState from '../../../components/nemonto/EmptyState';
import '../social/social-list-page.css';
import './chat-conversation-page.css';

const PAGE_LIMIT = 30;

const ChatConversationPage = () => {
  const navigate = useNavigate();
  const { type, id } = useParams();
  const location = useLocation();
  const userDataRaw = useSelector(userDataSelector);
  const currentUser = getCurrentUser(userDataRaw);

  const isGroup = type === 'group';

  const [header, setHeader] = useState(location.state?.header || null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [text, setText] = useState('');
  const [uploading, setUploading] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null);
  const [openReactionFor, setOpenReactionFor] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const socketRef = useRef(null);
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const seenIdsRef = useRef(new Set());

  useEffect(() => {
    seenIdsRef.current = new Set();
    setMessages([]);
    setHeader(location.state?.header || null);
    if (!location.state?.header) fetchHeaderFallback();
    loadMessages(1, true);
    markAllRead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, id]);

  useEffect(() => {
    const socket = io(process.env.REACT_APP_API_BASE_URL);
    socketRef.current = socket;

    socket.on('connect', () => {
      if (isGroup) socket.emit('joinRoom', id);
      else socket.emit('join', currentUser?.id);
    });

    if (isGroup) {
      socket.on('groupMessage', (incoming) => {
        const chat = incoming?.chat || incoming;
        if (!chat || String(chat.group_id) !== String(id)) return;
        appendMessage(chat);
      });
    } else {
      socket.on('privateMessage', (incoming) => {
        const chat = incoming?.chat || incoming;
        if (!chat) return;
        const otherId = Number(id);
        const meId = Number(currentUser?.id);
        const involves =
          Number(chat.sender_id) === otherId || (Number(chat.sender_id) === meId && Number(chat.receiver_id) === otherId);
        if (!involves) return;
        appendMessage(chat);
      });
    }

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, id]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages.length]);

  function appendMessage(chat) {
    if (!chat?.id || seenIdsRef.current.has(chat.id)) return;
    seenIdsRef.current.add(chat.id);
    setMessages((prev) => [...prev, chat]);
  }

  async function fetchHeaderFallback() {
    try {
      const res = await apiHandler(CHAT_USERS, 'POST', { page: 1, limit: 100 });
      if (apiSucceeded(res)) {
        const list = res?.data?.data?.chat_users?.data || [];
        const found = isGroup
          ? list.find((e) => String(e?.group_id) === String(id))
          : list.find((e) => String(e?.member_id) === String(id));
        if (found) {
          setHeader(
            isGroup
              ? { title: found?.user_group?.name || 'Group Chat', avatarUrl: found?.user_group?.image, isGroup: true }
              : { title: fullName(found?.member), avatarUrl: found?.member?.user_setting?.photo, isGroup: false },
          );
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function loadMessages(pageNo, replace) {
    setLoading(true);
    try {
      const params = isGroup
        ? { group_id: id, page: pageNo, limit: PAGE_LIMIT }
        : { other_user_id: id, page: pageNo, limit: PAGE_LIMIT };
      const res = await apiHandler(CHAT_SINGLE_USER, 'POST', params);
      if (apiSucceeded(res)) {
        const chats = res?.data?.data?.chats?.data || [];
        const meta = res?.data?.data?.chats?.meta;
        const chronological = [...chats].reverse();
        chronological.forEach((c) => seenIdsRef.current.add(c.id));
        setMessages((prev) => (replace ? chronological : [...chronological, ...prev]));
        setHasMore(!!meta && meta.current_page < meta.last_page);
        setPage(pageNo);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  async function markAllRead() {
    try {
      await apiHandler(CHAT_READ_ALL, 'POST', isGroup ? { group_id: id } : { other_user_id: id });
    } catch (error) {
      console.error(error);
    }
  }

  function resetComposer() {
    setReplyingTo(null);
    setEditingMessage(null);
    setText('');
  }

  function startReply(chat) {
    setEditingMessage(null);
    setOpenReactionFor(null);
    setReplyingTo({
      id: chat?.id,
      message: getMessageText(chat),
      senderName: fullName(chat?.sender),
      senderId: chat?.sender_id,
    });
  }

  function startEdit(chat) {
    setReplyingTo(null);
    setOpenReactionFor(null);
    setEditingMessage({ id: chat?.id });
    setText(getMessageText(chat));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed) return;

    if (editingMessage) {
      try {
        const res = await apiHandler(CHAT_EDIT, 'POST', { chat_id: editingMessage.id, message: trimmed });
        if (apiSucceeded(res)) {
          const updated = res?.data?.data?.chat || {};
          setMessages((prev) =>
            prev.map((c) => (c.id === editingMessage.id ? { ...c, ...updated, message: updated.message || trimmed, is_edited: 1 } : c)),
          );
          resetComposer();
        }
      } catch (error) {
        console.error(error);
      }
      return;
    }

    setText('');
    try {
      let res;
      if (replyingTo) {
        const receiverId = isGroup ? replyingTo.senderId : id;
        res = await apiHandler(CHAT_REPLY, 'POST', {
          receiver_id: receiverId,
          message: trimmed,
          reply_to_id: replyingTo.id,
          group_id: isGroup ? id : '',
        });
      } else if (isGroup) {
        res = await apiHandler(GROUP_CHAT_SAVE, 'POST', { group_id: id, message: trimmed });
      } else {
        res = await apiHandler(CHAT_SAVE, 'POST', { sender_id: currentUser?.id, receiver_id: id, message: trimmed });
      }

      if (apiSucceeded(res)) {
        const chat = res?.data?.data?.chat;
        appendMessage(
          replyingTo ? { ...chat, reply_to: { id: replyingTo.id, message: replyingTo.message, sender_name: replyingTo.senderName } } : chat,
        );
        setReplyingTo(null);
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function handleDelete(chat) {
    const result = await Swal.fire({
      title: 'Delete message?',
      text: 'This message will be deleted for everyone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    try {
      const res = await apiHandler(CHAT_DELETE, 'DELETE', { chat_id: chat.id });
      if (apiSucceeded(res)) {
        setMessages((prev) => prev.filter((c) => c.id !== chat.id));
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function handleReact(chat, option) {
    try {
      const res = await apiHandler(CHAT_REACT, 'POST', {
        chat_id: chat.id,
        message: getMessageText(chat),
        reaction_type: option.type,
        reaction: option.emoji,
        ...(isGroup ? { group_id: id } : {}),
      });
      if (apiSucceeded(res)) {
        const updated = res?.data?.data?.chat || {};
        setMessages((prev) =>
          prev.map((c) => {
            if (c.id !== chat.id) return c;
            const responseReactions = updated?.reactions || updated?.chat_reactions || updated?.user_chat_reactions || null;
            const merged = Array.isArray(responseReactions)
              ? responseReactions
              : [...(c.reactions || c.chat_reactions || c.user_chat_reactions || []), { reaction: option.emoji, reaction_type: option.type }];
            return { ...c, ...updated, reactions: merged };
          }),
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      setOpenReactionFor(null);
    }
  }

  const handlePickImage = () => fileInputRef.current?.click();

  async function handleImageSelected(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setUploading(true);
    try {
      const isVideo = file.type?.startsWith('video');
      const url = await handleImageUpload(file, isVideo ? 'chatVideos/' : 'chatImages/', file.type || 'image/jpeg');
      const createRes = isGroup
        ? await apiHandler(GROUP_CHAT_SAVE, 'POST', { group_id: id, message: '' })
        : await apiHandler(CHAT_SAVE, 'POST', { sender_id: currentUser?.id, receiver_id: id, message: '' });
      const chatId = createRes?.data?.data?.chat?.id;
      if (chatId) {
        const mediaRes = await apiHandler(CHAT_MEDIA_SAVE, 'POST', {
          chat_id: chatId,
          path: url,
          media_type: isVideo ? 1 : 0,
        });
        appendMessage(mediaRes?.data?.data?.chat || createRes?.data?.data?.chat);
      }
    } catch (error) {
      console.error('Chat media upload failed:', error);
    } finally {
      setUploading(false);
    }
  }

  const headerTitle = header?.title || (isGroup ? 'Group Chat' : 'Chat');
  const headerAvatar = header?.avatarUrl || NO_IMAGE;

  return (
    <div className="nmcc-root">
      <div className="nmcc-header">
        <button type="button" className="nmcc-icon-btn nmcc-back-btn" onClick={() => navigate('/chat')} title="Back to conversations">
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>arrow_back</span>
        </button>
        <img
          src={headerAvatar}
          alt={headerTitle}
          className="nmcc-header-avatar"
          onClick={() => isGroup && navigate(`/groups/${id}`)}
          style={{ cursor: isGroup ? 'pointer' : 'default' }}
        />
        <h5
          className="nmcc-header-name"
          onClick={() => isGroup && navigate(`/groups/${id}`)}
          style={{ cursor: isGroup ? 'pointer' : 'default' }}
        >
          {headerTitle}
        </h5>
        {isGroup && (
          <button type="button" className="nmcc-icon-btn" onClick={() => navigate(`/groups/${id}`)} title="Group members">
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>group</span>
          </button>
        )}
      </div>

      <div className="nmcc-messages">
        {hasMore && (
          <div className="nmcc-load-more-wrap">
            <button type="button" className="nmcc-load-more-btn" onClick={() => loadMessages(page + 1, false)} disabled={loading}>
              {loading ? 'Loading…' : 'Load older messages'}
            </button>
          </div>
        )}

        {!loading && messages.length === 0 && (
          <div className="nmcc-empty">
            <EmptyState icon="chat" title="No messages yet" sub="Say hello to start the conversation." />
          </div>
        )}

        {messages.map((chat) => {
          const isMine = chat?.sender_id === currentUser?.id;
          const replyData = getReplyMessageData(chat, messages);
          const reactionSummary = getReactionSummary(chat);
          const isEdited = chat?.is_edited === 1 || chat?.is_edited === '1';

          return (
            <div key={chat.id} className={`nmcc-row ${isMine ? 'nmcc-mine' : 'nmcc-theirs'}`}>
              {!isMine && (
                <img
                  src={chat?.sender?.user_setting?.photo || NO_IMAGE}
                  alt={fullName(chat?.sender)}
                  className="nmcc-row-avatar"
                />
              )}
              <div className="nmcc-bubble-group">
                {isGroup && !isMine && <div className="nmcc-sender-name">{fullName(chat?.sender)}</div>}

                <div className="nmcc-bubble-wrap">
                  <div className="nmcc-bubble">
                    {replyData && (
                      <div className="nmcc-reply-preview">
                        <div className="nmcc-reply-sender">{replyData.senderName}</div>
                        <div className="nmcc-reply-text">{replyData.message || 'Message'}</div>
                      </div>
                    )}
                    {(chat?.user_chat_images || []).map((img) =>
                      img?.media_type === 1 ? (
                        <video key={img.id || img.image_path} src={img.image_path} controls className="nmcc-bubble-media" />
                      ) : (
                        <img key={img.id || img.image_path} src={img.image_path} alt="attachment" className="nmcc-bubble-media" />
                      ),
                    )}
                    {chat?.message && <span>{chat.message}</span>}
                  </div>

                  <div className="nmcc-msg-actions">
                    <button type="button" className="nmcc-msg-action-btn" title="React" onClick={() => setOpenReactionFor(openReactionFor === chat.id ? null : chat.id)}>
                      <span className="material-symbols-outlined" style={{ fontSize: 15 }}>mood</span>
                    </button>
                    <button type="button" className="nmcc-msg-action-btn" title="Reply" onClick={() => startReply(chat)}>
                      <span className="material-symbols-outlined" style={{ fontSize: 15 }}>reply</span>
                    </button>
                    {isMine && chat?.message && (
                      <button type="button" className="nmcc-msg-action-btn" title="Edit" onClick={() => startEdit(chat)}>
                        <span className="material-symbols-outlined" style={{ fontSize: 15 }}>edit</span>
                      </button>
                    )}
                    {isMine && (
                      <button type="button" className="nmcc-msg-action-btn" title="Delete" onClick={() => handleDelete(chat)}>
                        <span className="material-symbols-outlined" style={{ fontSize: 15 }}>delete</span>
                      </button>
                    )}
                  </div>

                  {openReactionFor === chat.id && (
                    <div className="nmcc-reaction-picker">
                      {REACTION_OPTIONS.map((option) => (
                        <button key={option.type} type="button" className="nmcc-reaction-option" title={option.label} onClick={() => handleReact(chat, option)}>
                          {option.emoji}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {reactionSummary && (
                  <div className="nmcc-reaction-badge">
                    {reactionSummary.emoji} {reactionSummary.count > 1 ? reactionSummary.count : ''}
                  </div>
                )}

                <div className="nmcc-time">
                  {formatChatTime(chat?.created_at)}
                  {isEdited && <span className="nmcc-edited"> · edited</span>}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {(replyingTo || editingMessage) && (
        <div className="nmcc-composer-context">
          <div>
            <div className="nmcc-composer-context-label">{editingMessage ? 'Editing message' : `Replying to ${replyingTo?.senderName || 'User'}`}</div>
            <div className="nmcc-composer-context-text">{editingMessage ? getMessageText(messages.find((m) => m.id === editingMessage.id)) : replyingTo?.message}</div>
          </div>
          <button type="button" className="nmcc-composer-context-close" onClick={resetComposer}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>close</span>
          </button>
        </div>
      )}

      {showEmojiPicker && (
        <div className="nmcc-emoji-popover">
          <EmojiPicker
            width="100%"
            height={320}
            onEmojiClick={(emojiData) => setText((prev) => prev + emojiData.emoji)}
          />
        </div>
      )}

      <form onSubmit={handleSubmit} className="nmcc-input-bar">
        <input ref={fileInputRef} type="file" accept="image/*,video/*" style={{ display: 'none' }} onChange={handleImageSelected} />
        <button type="button" className="nmcc-icon-btn" onClick={() => setShowEmojiPicker((v) => !v)} title="Emoji">
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>mood</span>
        </button>
        <button type="button" className="nmcc-icon-btn" onClick={handlePickImage} disabled={uploading} title="Send image or video">
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{uploading ? 'hourglass_top' : 'image'}</span>
        </button>
        <input
          type="text"
          className="nmcc-text-input"
          placeholder="Type a message…"
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <button type="submit" className="nmcc-send-btn" disabled={!text.trim()}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>send</span>
        </button>
      </form>
    </div>
  );
};

export default ChatConversationPage;
